import { useQuery } from "@tanstack/react-query";

import { queryClient } from "@/lib/query/queryClient";

import {
  ClientPortalAPI,
  FirmCorrectionsAPI,
  FirmPortalAPI,
  FirmSignaturesAPI
} from "./api";

export const CLIENT_PORTAL_KEY = ["client-portal"] as const;

export const clientPortalKeys = {
  me: () => [...CLIENT_PORTAL_KEY, "me"] as const,
  home: () => [...CLIENT_PORTAL_KEY, "home"] as const,
  documents: () => [...CLIENT_PORTAL_KEY, "documents"] as const,
  profile: () => [...CLIENT_PORTAL_KEY, "profile"] as const,
  caseDetail: (id: string) => [...CLIENT_PORTAL_KEY, "case", id] as const,
  firmState: (clientId: string) =>
    [...CLIENT_PORTAL_KEY, "firm", clientId, "state"] as const,
  firmCases: (clientId: string) =>
    [...CLIENT_PORTAL_KEY, "firm", clientId, "cases"] as const,
  firmCorrections: (clientId: string) =>
    [...CLIENT_PORTAL_KEY, "firm", clientId, "corrections"] as const,
  signature: (id: string) => [...CLIENT_PORTAL_KEY, "signature", id] as const,
  signatureFile: (id: string, status: string) =>
    [...CLIENT_PORTAL_KEY, "signature", id, "file", status] as const,
  firmSignatures: (clientId: string) =>
    [...CLIENT_PORTAL_KEY, "firm", clientId, "signatures"] as const
};

export const refreshClientPortal = (): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: CLIENT_PORTAL_KEY });

export const useClientPortalMe = (enabled = true) =>
  useQuery(
    {
      queryKey: clientPortalKeys.me(),
      queryFn: ClientPortalAPI.me,
      enabled,
      retry: false
    },
    queryClient
  );

export const useClientPortalHome = () =>
  useQuery(
    { queryKey: clientPortalKeys.home(), queryFn: ClientPortalAPI.home },
    queryClient
  );

export const useClientPortalDocuments = () =>
  useQuery(
    {
      queryKey: clientPortalKeys.documents(),
      queryFn: ClientPortalAPI.documents
    },
    queryClient
  );

export const useClientPortalCase = (caseId: string | undefined) =>
  useQuery(
    {
      queryKey: clientPortalKeys.caseDetail(caseId ?? ""),
      queryFn: () => ClientPortalAPI.caseDetail(caseId ?? ""),
      enabled: Boolean(caseId)
    },
    queryClient
  );

export const useFirmPortalState = (clientId: string) =>
  useQuery(
    {
      queryKey: clientPortalKeys.firmState(clientId),
      queryFn: () => FirmPortalAPI.state(clientId),
      enabled: Boolean(clientId)
    },
    queryClient
  );

export const useFirmPortalCases = (clientId: string) =>
  useQuery(
    {
      queryKey: clientPortalKeys.firmCases(clientId),
      queryFn: () => FirmPortalAPI.cases(clientId),
      enabled: Boolean(clientId)
    },
    queryClient
  );

export const useClientPortalProfile = () =>
  useQuery(
    { queryKey: clientPortalKeys.profile(), queryFn: ClientPortalAPI.profile },
    queryClient
  );

export const useClientPortalSignature = (id: string | undefined) =>
  useQuery(
    {
      queryKey: clientPortalKeys.signature(id ?? ""),
      queryFn: () => ClientPortalAPI.signature(id ?? ""),
      enabled: Boolean(id),
      retry: false
    },
    queryClient
  );

/** Keyed by status so signing swaps the document for the signed copy. */
export const useClientPortalSignatureFile = (
  id: string | undefined,
  status: string | undefined
) =>
  useQuery(
    {
      queryKey: clientPortalKeys.signatureFile(id ?? "", status ?? ""),
      queryFn: () => ClientPortalAPI.signatureFile(id ?? ""),
      enabled: Boolean(id && status),
      staleTime: Infinity
    },
    queryClient
  );

export const useFirmSignatures = (clientId: string) =>
  useQuery(
    {
      queryKey: clientPortalKeys.firmSignatures(clientId),
      queryFn: () => FirmSignaturesAPI.list(clientId),
      enabled: Boolean(clientId)
    },
    queryClient
  );

export const useFirmCorrections = (clientId: string) =>
  useQuery(
    {
      queryKey: clientPortalKeys.firmCorrections(clientId),
      queryFn: () => FirmCorrectionsAPI.list(clientId),
      enabled: Boolean(clientId)
    },
    queryClient
  );
